import { SearchSelectBar } from '@dealstack/frontend/shared/components/forms/SearchSelectBar/SearchSelectBar';
import { StringInput as StringInputV2 } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput.v2';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useOutsideClick } from '@dealstack/frontend/shared/other/react/hooks/useOutsideClick';
import { COUNTRIES_OPTIONS } from '@dealstack/shared/lib/countries/countriesData';
import { useEffect, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { ISearchSelectBar } from '@dealstack/frontend/shared/components/forms/SearchSelectBar/SearchSelectBar.types';
import { StringInputV2SizeValues } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput.v2.types';

interface Props extends Partial<ISearchSelectBar> {
	className?: string;
	label?: string;
	name: string;
	inputType?: string;
	inputPlaceHolder?: string;
	onInputChange: (input: string | number) => void;
	disabled?: boolean;
	readOnly?: boolean;
	size: StringInputV2SizeValues;
}

const COUNTRY_INPUT = 'countryInput';

const CountrySelect = ({
	name,
	label,
	className,
	disabled,
	readOnly,
	inputType,
	inputPlaceHolder,
	onInputChange,
	size,
}: Props) => {
	const [countryOption, setCountryOption] = useState(COUNTRIES_OPTIONS);
	const handleCountrySearch = (input: string | number) => {
		const filteredResult = COUNTRIES_OPTIONS.filter((each) =>
			each.label.toLowerCase().includes((input as string).toLowerCase())
		);
		setCountryOption(filteredResult);
	};

	const countryField = useController({ name });
	const countrySearchInputField = useController({ name: COUNTRY_INPUT });
	const [showCountryMenu, setShowCountryMenu] = useState(false);
	const tCommonUi = useTranslationCommonUi();
	const ref = useRef<HTMLDivElement>(null);
	const handleReset = (isInputFieldClicked = false) => {
		setShowCountryMenu(isInputFieldClicked ? !showCountryMenu : false);
		countrySearchInputField.field.onChange('');
		setCountryOption(COUNTRIES_OPTIONS);
	};
	useOutsideClick<HTMLDivElement>(ref, handleReset);

	useEffect(() => {
		if (countryField.field.value) {
			handleReset();
		}
	}, [countryField.field.value]);
	return (
		<div className={className}>
			<InputWrapper ref={ref}>
				<StringInputV2
					label={label}
					readOnly={readOnly}
					disabled={disabled}
					name={name}
					inputType={inputType}
					inputPlaceHolder={inputPlaceHolder}
					onInputChange={onInputChange}
					leftIcon={!readOnly ? 'up-down-arrow' : null}
					onClick={() => {
						if (!readOnly) {
							handleReset(true);
						}
					}}
					size={size}
				/>

				{showCountryMenu && (
					<SearchSelectBar
						size={size}
						options={countryOption}
						onSearchInputChange={handleCountrySearch}
						maximumHeightForOption={140}
						showSearchBar={true}
						showScrollBar={true}
						searchSelectBarInputName={COUNTRY_INPUT}
						inputPlaceHolderText={tCommonUi('country_search')}
						onSelectOption={countryField.field.onChange}
					/>
				)}
			</InputWrapper>
		</div>
	);
};

const InputWrapper = styled.div<{
	isError?: boolean;
}>`
	position: relative;
	width: 100%;
`;

const Styled = styled(CountrySelect)`
	${SearchSelectBar} {
		position: absolute;
		z-index: 9999;
		cursor: pointer;
	}

	${(props) =>
		props.disabled &&
		css`
			pointer-events: none;
		`}
`;

export { Styled as CountrySelect };
